// src/components/Properties.js
import { useEffect, useRef, useMemo, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Form, Button, Modal, ProgressBar, Alert } from "react-bootstrap";
import { useJsApiLoader } from "@react-google-maps/api";

import { propertiesAPI } from "./PropertiesAPI";
import "./properties.css";
import "../AppItem.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import LoadingAnimation from "../LoadingAnimation";
import statesBounds from "../states.json";
import PropertyDetailsModal from "./PropertyDetailsModal";
import PropertyMapModal from "./PropertyMapModal";
import FilterForm from "./FilterForms";
import DocumentsTable from "../DocumentsTable";
import AdvancedMarkersMap from "../GoogleMap";
import { GOOGLE_MAP_LIBRARIES } from "../../config/maps";

import { generateSaleId, isPropertyRegistered, getDateRange } from "../utils";
import { fetchUserFavorites, removeFavorite, addFavorite } from "../../redux/favoritesSlice";
import { findFavorite, isPropertyFavorite } from "../../utilities/favoritesUtils";
import {
  fetchUserRegistrations,
  registerForSaleGlobalRegistration,
  unregisterFromSale,
} from "../../redux/registrationSlice";
import {
  setSelectedState,
  setSelectedCounty,
  setSelectedSaleType,
  setSelectedTimeFrame,
  resetFilters,
} from "../../redux/filterSlice";
import { CountyApi } from "../../redux/countySlice";
import { SearchX } from "lucide-react";

// Extracted components
import PropertyList from "./PropertyList";
import FavoritesList from "./FavoritesList";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import NoticeToast from "./shared/NoticeToast";
import useToast from "../../hooks/useToast";

// Constants
import { SALE_TYPE_URL_TO_ID, SALE_TYPE_ID_TO_URL } from "../../constants/saleTypes";
import { FUTURE_TIME_FRAMES, TIME_FRAMES, isPastTimeFrame } from "../../constants/timeFrames";

// --- Properties component ---
const Properties = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  useDocumentTitle(searchParams.get("modal") === "auctionList" ? "Auction List" : "Properties");

  // Keep a ref to searchParams so updateSearchParams always reads the latest
  // value without needing it in dependency arrays (setSearchParams does not
  // support the functional updater form in react-router-dom v6)
  const searchParamsRef = useRef(searchParams);
  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  // Redux state
  const user = useSelector((state) => state.user.user);
  const userId = user?.fusionauth_user_id ?? null;
  const accessToken = useSelector((state) => state.user.tokens.accessToken);
  const authInitialized = useSelector((state) => state.user.authInitialized);
  const favorites = useSelector((state) => state.favorites.favorites);
  const favoritesLoading = useSelector((state) => state.favorites.loading);
  const favoritesError = useSelector((state) => state.favorites.error);
  const registrations = useSelector((state) => state.registration.registrations);
  const registrationsLoading = useSelector((state) => state.registration.loading);
  const registrationsError = useSelector((state) => state.registration.error);
  const selectedState = useSelector((state) => state.filter.selectedState);
  const selectedCounty = useSelector((state) => state.filter.selectedCounty);
  const selectedSaleType = useSelector((state) => state.filter.selectedSaleType);
  const selectedTimeFrame = useSelector((state) => state.filter.selectedTimeFrame);

  // Local state
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [saleTypes, setSaleTypes] = useState([]);
  const [propertiesData, setPropertiesData] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [showMapToggle, setShowMapToggle] = useState(searchParams.get("openMap") === "true");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showExpiredSales] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [gateStatus, setGateStatus] = useState(null);

  // Map / directions
  const [directions, setDirections] = useState(null);
  const [showDirections, setShowDirections] = useState(false);

  // Property detail modal
  const [detailsResponse, setDetailsResponse] = useState(null);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [propertyDocuments, setPropertyDocuments] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);

  // URL params
  const idFromUrl = searchParams.get("id");
  const propertyIdFromUrl = searchParams.get("propertyId");
  const saleIdFromUrl = searchParams.get("saleId");
  const stateFromUrl = searchParams.get("state");
  const countyFromUrl = searchParams.get("county");
  const saleTypeFromUrl = searchParams.get("saleType");
  const timeFrameFromUrl = searchParams.get("timeFrame");

  // Google Maps
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API,
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  // Computed once here (not duplicated in FilterForm.jsx) so both this
  // component's county-sales query and FilterForm.jsx's dropdown query
  // share an identical cache key — avoids a desync if the two independently
  // computed the date range on opposite sides of a midnight rollover.
  const isPast = isPastTimeFrame(selectedTimeFrame);
  const { startDate, endDate } = isPast ? getDateRange(selectedTimeFrame) : { startDate: "", endDate: "" };

  const {
    data: upcomingShortList,
    isUninitialized: upcomingUninitialized,
    isFetching: upcomingFetching,
    isError: upcomingIsError,
  } = CountyApi.endpoints.getCountySales.useQueryState(
    { stateCode: selectedState, countyName: selectedCounty },
    { skip: !selectedState || !selectedCounty || isPast }
  );

  const {
    data: pastShortList,
    isUninitialized: pastUninitialized,
    isFetching: pastFetching,
    isError: pastIsError,
  } = CountyApi.endpoints.getCountySalesByDateRange.useQueryState(
    { stateCode: selectedState, countyName: selectedCounty, startDate, endDate },
    { skip: !selectedState || !selectedCounty || !isPast }
  );

  const propertyShortList = isPast ? pastShortList : upcomingShortList;
  const isUninitialized = isPast ? pastUninitialized : upcomingUninitialized;
  const isFetching = isPast ? pastFetching : upcomingFetching;
  const isError = isPast ? pastIsError : upcomingIsError;

  const hasLoadedProperties =
    Array.isArray(propertiesData) && propertiesData.length > 0 && propertiesData.some((p) => p?.propertyId || p?.id);

  const hasCountyContext = !!selectedState && !!selectedCounty;
  const countySalesReady = hasCountyContext && !isUninitialized && !isFetching && !isError;
  const noSalesInCounty = countySalesReady && (propertyShortList?.length ?? 0) === 0;
  const showSearchBar = !noSalesInCounty && hasLoadedProperties;

  const formattedCountyName = selectedCounty
    .split(" ")
    .map((word) => { return word.charAt(0).toUpperCase() + word.slice(1) })
    .join(" ");

  const matchedCounty = counties.find(
    (c) => c.id.toLowerCase() === selectedCounty.toLowerCase() && c.joinId === selectedState
  );

  const countyDisplayName = matchedCounty?.name ?? `${formattedCountyName} County`;

  /* -------------------------
     URL → Redux sync
  ------------------------- */
  useEffect(() => {
    if (stateFromUrl && countyFromUrl && saleTypeFromUrl) {
      dispatch(setSelectedState(stateFromUrl));
      dispatch(setSelectedCounty(countyFromUrl));

      const matchedSaleTypeId = SALE_TYPE_URL_TO_ID[saleTypeFromUrl.trim().toLowerCase()];
      if (matchedSaleTypeId) {
        dispatch(setSelectedSaleType(matchedSaleTypeId));
      } else {
        console.warn(`No matching sale type for "${saleTypeFromUrl}"`);
      }
      // Restore timeFrame from URL if present, otherwise default
      dispatch(setSelectedTimeFrame(timeFrameFromUrl || "All Future Sale Dates"));
    }
  }, [stateFromUrl, countyFromUrl, saleTypeFromUrl, timeFrameFromUrl, dispatch]);

  // Once auth is confirmed and user is logged in, restore showFavoritesOnly
  // from the URL — we can't do this at initialization time because userId
  // is not yet available when the component first mounts.
  useEffect(() => {
    if (!authInitialized || !userId) return;
    if (searchParams.get("showFavorites") === "true") {
      setShowFavoritesOnly(true);
    }
  }, [authInitialized, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once auth is confirmed and user is logged in, restore showFavoritesOnly
  // from the URL — we can't do this at initialization time because userId
  // is not yet available when the component first mounts.
  useEffect(() => {
    if (!authInitialized || !userId) return;
    if (searchParams.get("showFavorites") === "true") {
      setShowFavoritesOnly(true);
    }
  }, [authInitialized, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // If user is not logged in, quietly remove showFavorites from the URL
  // so the param doesn't linger for logged-out users or shared links.
  // Also reset the toggle state in case the session expired mid-session.
  // Guard against premature firing during auth initialization:
  //   - Both empty = still initializing, don't run
  //   - Token exists but no userId = fetchUserInfo still in flight, don't run
  //   - Neither exists after init = genuinely logged out, run cleanup
  useEffect(() => {
    if (!authInitialized) return; // still initializing
    if (accessToken && !userId) return; // token loaded, user fetch in flight
    if (!userId) {
      if (searchParams.get("showFavorites") === "true") {
        const p = new URLSearchParams(searchParamsRef.current);
        p.delete("showFavorites");
        setSearchParams(p);
      }
      setShowFavoritesOnly(false);
    }
  }, [userId, accessToken, authInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open modal if URL has the right params.
  // Guard: only open when the URL fully identifies a property — bail out if any
  // param is missing so we don't accidentally match on null/undefined fields.
  // Coerce to String on both sides: URL params from searchParams.get() are always
  // strings, while p.id / p.saleId may come back from the API as numbers.
  useEffect(() => {
    if (!idFromUrl || !saleIdFromUrl || !propertyIdFromUrl) return;
    const target = propertiesData.find(
      (p) => String(p.id) === String(idFromUrl) && String(p.saleId) === String(saleIdFromUrl) && String(p.propertyId) === String(propertyIdFromUrl)
    );
    if (target && Object.keys(target).length > 0) fetchPropertyDetails(target);
  }, [propertiesData]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -------------------------
     Data fetching
  ------------------------- */
  const fetchProperties = async () => {
    setIsLoading(true);
    setPropertiesData([]); // clear stale data before fetching fresh data
    if (!selectedState || !selectedCounty || !selectedSaleType) {
      setPropertiesData([]);
      setGateStatus(null);
      setIsLoading(false);
      return;
    }
    try {
      const response = await propertiesAPI.getCardData({
        auctionDateRange: getDateRange(selectedTimeFrame),
        auctionStyle: "",
        county: selectedCounty,
        propertySaleType: selectedSaleType,
        recordCount: 50000,
        saleStatus: "",
        searchText: "",
        startIndex: 0,
        state: selectedState,
      });

      console.log("FULL RESPONSE:", response);

      setGateStatus(response?.gateStatus ?? null);

      if (!response?.properties) {
        console.warn("No properties found in API response.");
        return;
      }

      // Deduplicate by id+saleId
      const seen = new Set();
      const unique = response.properties.filter((p) => {
        const key = `${p.id}-${p.saleId}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // Enrich with effective/generated sale IDs
      const enriched = unique.map((prop) => {
        const parsedOriginalSaleId = prop.originalSaleId != null ? Number(prop.originalSaleId) : null;
        const parsedSaleId = prop.saleId != null ? Number(prop.saleId) : null;
        return {
          ...prop,
          originalSaleId: parsedOriginalSaleId,
          generatedSaleId: parsedOriginalSaleId != null ? String(parsedOriginalSaleId) : generateSaleId(prop),
          effectiveSaleId:
            parsedOriginalSaleId != null
              ? parsedOriginalSaleId
              : parsedSaleId != null
              ? parsedSaleId
              : generateSaleId(prop),
        };
      });

      setPropertiesData(enriched);
    } catch (err) {
      console.error("Error fetching properties:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Static reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        setStates(await propertiesAPI.getStates());
      } catch (e) {
        console.error("Error fetching states:", e);
      }
      try {
        setSaleTypes(await propertiesAPI.getPropertySaleTypes());
      } catch (e) {
        console.error("Error fetching sale types:", e);
      }
    };
    loadReferenceData();
  }, []);

  // Favourites + registrations when logged in
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserFavorites());
      dispatch(fetchUserRegistrations());
    }
  }, [userId, dispatch]);

  // Properties when filters change
  useEffect(() => {
    if (selectedState && selectedCounty && selectedSaleType) fetchProperties();
  }, [selectedState, selectedCounty, selectedSaleType, selectedTimeFrame]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset stale data on filter change
  useEffect(() => {
    setPropertiesData([]);
    setGateStatus(null);
  }, [selectedState, selectedCounty, selectedSaleType, selectedTimeFrame]);

  // Counties for the selected state
  useEffect(() => {
    const loadCounties = async () => {
      try {
        const all = await propertiesAPI.getCounties();
        const filtered = all.filter((c) => !c.name.toLowerCase().startsWith("zzz"));
        setCounties(selectedState ? filtered.filter((c) => String(c.joinId) === selectedState) : filtered);
      } catch (err) {
        console.error("Error fetching counties:", err);
      }
    };
    loadCounties();
  }, [selectedState]);

  /* -------------------------
     Local search filter
  ------------------------- */
  useEffect(() => {
    if (!searchText) {
      setFilteredProperties(propertiesData);
      return;
    }
    const low = searchText.toLowerCase();
    setFilteredProperties(
      propertiesData.filter(
        (p) =>
          [p.address1, p.city, p.state, p.zip, p.displaySaleId, p.briefLegal].some((field) =>
            field?.toLowerCase().includes(low)
          ) ||
          p.propertyId?.toString().toLowerCase().includes(low) ||
          p.altPropertyId?.toString().toLowerCase().includes(low)
      )
    );
  }, [searchText, propertiesData]);

  /* -------------------------
     Redux error → toast
  ------------------------- */
  useEffect(() => {
    if (favoritesError) toast.triggerFromPayload(favoritesError);
  }, [favoritesError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (registrationsError) toast.triggerFromPayload(registrationsError);
  }, [registrationsError]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -------------------------
     Map helpers
  ------------------------- */
  const mapFitPoints = useMemo(() => {
    const points = filteredProperties
      .map((p) => {
        const lat = Number(p?.latitude ?? p?.coordinates?.lat);
        const lng = Number(p?.longitude ?? p?.coordinates?.lng);
        return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
      })
      .filter(Boolean);

    if (points.length > 0) return points;

    const sb = statesBounds.find((item) => item.fips === selectedState)?.bounds;
    if (!sb) return null;
    return [
      { lat: sb[0][1], lng: sb[0][0] },
      { lat: sb[1][1], lng: sb[1][0] },
    ];
  }, [filteredProperties, selectedState]);

  const fetchDirections = (start, end) => {
    const service = new window.google.maps.DirectionsService();
    service.route(
      { origin: start, destination: end, travelMode: window.google.maps.TravelMode.DRIVING },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
          setShowDirections(true);
        } else {
          console.error(`Error fetching directions: ${status}`);
        }
      }
    );
  };

  const handleGetDirections = (prop) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        fetchDirections(
          { lat: pos.coords.latitude, lng: pos.coords.longitude },
          { lat: prop.latitude, lng: prop.longitude }
        ),
      () => console.error("Error getting user location")
    );
  };

  /* -------------------------
     Filter handlers
  ------------------------- */
  const updateSearchParams = useCallback(
    (updates, deletions = []) => {
      const p = new URLSearchParams(searchParamsRef.current);
      Object.entries(updates).forEach(([k, v]) => p.set(k, v));
      deletions.forEach((k) => p.delete(k));
      setSearchParams(p);
    },
    [setSearchParams]
  );

  const handleStateChange = (e) => {
    const st = e.target.value;
    dispatch(setSelectedState(st));
    dispatch(setSelectedCounty(""));
    dispatch(setSelectedSaleType(""));
    dispatch(setSelectedTimeFrame("All Future Sale Dates"));
    updateSearchParams({ state: st }, ["county", "saleType", "timeFrame"]);
  };

  const handleCountyChange = (county) => {
    dispatch(setSelectedCounty(county));
    dispatch(setSelectedSaleType(""));
    dispatch(setSelectedTimeFrame("All Future Sale Dates"));
    updateSearchParams({ county }, ["saleType", "timeFrame"]);
  };

  const handleSaleTypeChange = (saleType) => {
    dispatch(setSelectedSaleType(saleType));
    if (SALE_TYPE_ID_TO_URL[saleType]) {
      updateSearchParams({ saleType: SALE_TYPE_ID_TO_URL[saleType] });
    }
  };

  const handleTimeFrameChange = (e) => {
    const timeFrame = e.target.value;
    dispatch(setSelectedTimeFrame(timeFrame));
    dispatch(setSelectedSaleType(""));
    updateSearchParams({ timeFrame }, ["saleType"]);
  };

  const resetDropdowns = () => {
    dispatch(resetFilters());
    const p = new URLSearchParams(searchParamsRef.current);
    [
      "state",
      "county",
      "saleType",
      "timeFrame",
      "modal",
      "id",
      "propertyId",
      "saleId",
      "openMap",
      "showFavorites",
    ].forEach((k) => p.delete(k));
    setSearchParams(p);
    setShowMapToggle(false);
    setShowFavoritesOnly(false);
  };

  /* -------------------------
     Expired sales filter
  ------------------------- */
  const filterExpiredSales = (arr) => {
    if (!FUTURE_TIME_FRAMES.includes(selectedTimeFrame)) return arr;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return arr.filter((p) => {
      const isCancelled = !!p.cancelled;
      const auctionDate = new Date(p.auctionDate);
      auctionDate.setHours(0, 0, 0, 0);
      if (isCancelled) {
        const cutoff = new Date(auctionDate);
        cutoff.setDate(cutoff.getDate() + 1);
        return showExpiredSales || today.getTime() <= cutoff.getTime();
      }
      return showExpiredSales || auctionDate.getTime() >= today.getTime();
    });
  };

  /* -------------------------
     Property detail modal
  ------------------------- */
  const handlePropertyDetailsModal = useCallback(
    (show, property) => {
      if (show) {
        updateSearchParams({
          modal: "propertydetails",
          id: property.id,
          propertyId: property.propertyId,
          saleId: Number(property.originalSaleId ?? property.saleId),
        });
      } else {
        const p = new URLSearchParams(searchParamsRef.current);
        ["modal", "id", "propertyId", "saleId"].forEach((k) => p.delete(k));
        setSearchParams(p);
      }
    },
    [updateSearchParams, setSearchParams]
  );

  const handleMapModal = useCallback(
    (show, property) => {
      if (show) {
        updateSearchParams({
          modal: "propertymap",
          id: property.id,
          propertyId: property.propertyId,
          saleId: Number(property.originalSaleId ?? property.saleId),
        });
      } else {
        const p = new URLSearchParams(searchParamsRef.current);
        ["modal", "id", "propertyId", "saleId"].forEach((k) => p.delete(k));
        setSearchParams(p);
      }
    },
    [updateSearchParams, setSearchParams]
  );

  const fetchPropertyDetails = async (property) => {
    try {
      const fullDetails = await propertiesAPI.getPropertyDetail({
        id: property.id,
        propertyId: property.propertyId,
        saleId: Number(property.originalSaleId ?? property.saleId),
        saleType: property.saleType,
      });
      setDetailsResponse(fullDetails);
      setPropertyDetails(fullDetails.propertyInfo);
      setSelectedProperty({ ...property, auctionDetail: fullDetails.auctionDetail });
    } catch (error) {
      console.error("Error fetching property details:", error);
    }
  };

  const handleDetailsClick = useCallback(
    async (property, propertyInfo, e) => {
      if (e?.preventDefault) e.preventDefault();
      if (!property) return;
      await fetchPropertyDetails(property);
      handlePropertyDetailsModal(true, property);
    },
    [handlePropertyDetailsModal]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMarkerClick = useCallback(
    (property) => handleDetailsClick(property, property.propertyInfo, null),
    [handleDetailsClick]
  );

  const handleMapClick = useCallback(
    async (property, propertyInfo, e) => {
      if (e?.preventDefault) e.preventDefault();
      if (!property) return;
      await fetchPropertyDetails(property);
      handleMapModal(true, property);
    },
    [handleMapModal]
  );

  /* -------------------------
     Favorites helpers
  ------------------------- */
  const isPropertyFavoriteFunc = useCallback((property) => isPropertyFavorite(favorites, property), [favorites]);

  const handleAddFavorite = async (property) => {
    if (!userId) {
      toast.triggerToast("Please log in to manage favorites.");
      return;
    }
    const action = await dispatch(addFavorite(property));
    if (addFavorite.rejected.match(action)) toast.triggerFromPayload(action.payload);
  };

  const handleRemoveFavorite = async (favoriteId) => {
    if (!userId) {
      toast.triggerToast("Please log in to manage favorites.");
      return;
    }
    const action = await dispatch(removeFavorite(favoriteId));
    if (removeFavorite.rejected.match(action)) toast.triggerFromPayload(action.payload);
  };

  /* -------------------------
     Registration helpers
  ------------------------- */
  const handleRegisterOrUnregisterClick = async ({ saleId, auction }) => {
    if (!userId) {
      toast.triggerToast("Please log in to register/unregister.");
      return;
    }

    if (isPropertyRegistered(auction, registrations)) {
      const action = await dispatch(unregisterFromSale(saleId));
      if (unregisterFromSale.rejected.match(action)) toast.triggerFromPayload(action.payload);
    } else {
      const action = await dispatch(registerForSaleGlobalRegistration({ saleId, auction }));
      if (registerForSaleGlobalRegistration.rejected.match(action)) toast.triggerFromPayload(action.payload);
    }
  };

  /* -------------------------
     Derived display state
  ------------------------- */

  const filtersApplied = !!selectedState && !!selectedCounty && (noSalesInCounty || !!selectedSaleType);
  const visibleProperties = filterExpiredSales(filteredProperties.length > 0 ? filteredProperties : propertiesData);
  const isGated = !!gateStatus && visibleProperties.length === 0;

  const formatFirstAdAppearsDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.slice(0, 10).split("-");
    return `${month}/${day}/${year}`;
  };

  const gateMessage = useMemo(() => {
    if (!isGated) return "";
    return gateStatus.firstAdAppears
      ? `Published list is not available at this time. Check back after ${formatFirstAdAppearsDate(gateStatus.firstAdAppears)}.`
      : "Published list is not available at this time. Check back closer to the sale date.";
  }, [isGated, gateStatus]);

  const resultsStatusMessage = useMemo(() => {
    if (!selectedState && !selectedCounty && !selectedSaleType) {
      return "";
    }
    if (!selectedState || !selectedCounty || !selectedSaleType) {
      return "Please select State, County, and Sale Type to view properties.";
    }
    if (isLoading) {
      return "Loading properties.";
    }
    if (noSalesInCounty) {
      return "No properties found.";
    }
    const count = visibleProperties.length;
    if (count === 0) {
      return isGated ? "" : "No properties match your search."; // CHANGED
    }
    return `Showing ${count} propert${count === 1 ? "y" : "ies"}.`;
  }, [
    selectedState,
    selectedCounty,
    selectedSaleType,
    isLoading,
    noSalesInCounty,
    visibleProperties.length,
  ]);

  /* -------------------------
     Render
  ------------------------- */
  return (
    <div>
      <h1 className="visually-hidden">Properties</h1>
      <div className="visually-hidden" aria-live="polite" aria-atomic="true">
        {resultsStatusMessage}
      </div>
      <NoticeToast
        show={toast.showToast}
        message={toast.toastMessage}
        missingFields={toast.missingFields}
        onClose={toast.dismissToast}
      />

      <center>
        <Button variant="primary" onClick={() => setShowDocumentModal(true)} className="custom-download-button my-3">
          <i className="bi bi-download" style={{ marginRight: "5px" }} />
          SB 156 Documents
        </Button>
      </center>

      <div className="propertiesContainer">
        {/* Filters */}
        <center>
          <FilterForm
            states={states}
            selectedState={selectedState}
            handleStateChange={handleStateChange}
            counties={counties}
            selectedCounty={selectedCounty}
            setSelectedCounty={handleCountyChange}
            saleTypes={saleTypes}
            selectedSaleType={selectedSaleType}
            setSelectedSaleType={handleSaleTypeChange}
            timeFrames={TIME_FRAMES}
            selectedTimeFrame={selectedTimeFrame}
            handleTimeFrameChange={handleTimeFrameChange}
            resetDropdowns={resetDropdowns}
            startDate={startDate}
            endDate={endDate}
          />
        </center>

        {/* Filter warnings */}
        {!selectedState && !selectedCounty && !selectedSaleType && (
          <Alert variant="warning">
            Please select <b>State</b>, <b>County</b>, and <b>Sale Type</b>.
          </Alert>
        )}
        {selectedState && !selectedCounty && !selectedSaleType && (
          <Alert variant="warning">
            Please select <b>County</b> and <b>Sale Type</b>.
          </Alert>
        )}
        {selectedState &&
          selectedCounty &&
          !isUninitialized &&
          (propertyShortList?.length ?? 0) > 0 &&
          !selectedSaleType && (
            <Alert variant="warning">
              Please select <b>Sale Type</b>.
            </Alert>
          )}

        {/* Search bar */}
        {noSalesInCounty ? (
          <p>No properties found.</p>
        ) : showSearchBar ? (
          <div className="search-container mb-3 d-flex justify-content-center">
            <Form.Label htmlFor="property-search" className="visually-hidden">
              Search properties
            </Form.Label>
            <Form.Control
              id="property-search"
              type="text"
              placeholder="Search properties..."
              aria-label="Search properties"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="mr-2 my-1 w-50"
            />
            <Button variant="outline-secondary" onClick={() => setSearchText("")}>
              Clear Search
            </Button>
          </div>
        ) : null}

        {/* Loading */}
        {isLoading && <ProgressBar now={progress} label={`${Math.round(progress)}%`} />}

        {/* View toggles */}
        <Form.Check
          type="switch"
          id="map-view-switch"
          label="Map View"
          checked={showMapToggle}
          onChange={() => {
            const next = !showMapToggle;
            setShowMapToggle(next);
            if (next) {
              updateSearchParams({ openMap: "true" });
            } else {
              const p = new URLSearchParams(searchParamsRef.current);
              p.delete("openMap");
              setSearchParams(p);
            }
          }}
          className="mb-3 d-flex justify-content-center"
        />
        {userId && (
          <Form.Check
            type="switch"
            id="favorites-switch"
            label="Show Favorites Only"
            checked={showFavoritesOnly}
            onChange={() => {
              const next = !showFavoritesOnly;
              setShowFavoritesOnly(next);
              if (next) {
                updateSearchParams({ showFavorites: "true" });
              } else {
                const p = new URLSearchParams(searchParamsRef.current);
                p.delete("showFavorites");
                setSearchParams(p);
              }
            }}
            className="mb-3 d-flex justify-content-center"
          />
        )}

        {!noSalesInCounty && registrationsLoading && <LoadingAnimation />}
        {!noSalesInCounty && favoritesLoading && <LoadingAnimation />}

        {/* Main content */}
        {showMapToggle ? (
          !isLoaded ? (
            <div style={{ height: "400px" }}>{loadError ? "Failed to load Google Maps" : "Loading map..."}</div>
          ) : (
            <AdvancedMarkersMap
              properties={filteredProperties}
              onMarkerClick={handleMarkerClick}
              directions={directions}
              showDirections={showDirections}
              fitPoints={mapFitPoints}
            />
          )
        ) : showFavoritesOnly ? (
          <FavoritesList
            favorites={favorites}
            propertiesData={propertiesData}
            registrations={registrations}
            onDetailsClick={handleDetailsClick}
            onRegisterClick={handleRegisterOrUnregisterClick}
            onMapClick={handleMapClick}
            onAddFavorite={handleAddFavorite}
            onRemoveFavorite={handleRemoveFavorite}
            isPropertyFavorite={isPropertyFavoriteFunc}
            isPropertyRegistered={isPropertyRegistered}
            triggerToast={toast.triggerToast}
          />
        ) : filtersApplied && visibleProperties.length > 0 ? (
          <PropertyList
            properties={visibleProperties}
            selectedState={selectedState}
            selectedTimeFrame={selectedTimeFrame}
            favorites={favorites}
            registrations={registrations}
            onDetailsClick={handleDetailsClick}
            onRegisterClick={handleRegisterOrUnregisterClick}
            onMapClick={handleMapClick}
            onAddFavorite={handleAddFavorite}
            onRemoveFavorite={handleRemoveFavorite}
            isPropertyFavorite={isPropertyFavoriteFunc}
            isPropertyRegistered={isPropertyRegistered}
            triggerToast={toast.triggerToast}
          />
        ) : filtersApplied && !isLoading && !noSalesInCounty && isGated ? (
          <div
            className="empty-state d-flex flex-row align-items-center text-center py-4 w-100 justify-content-center"
            aria-live="polite"
            aria-atomic="true"
          >
            <SearchX
              className="empty-state-icon pe-2"
              aria-hidden="true"
              color="#c84315"
              size={28}
            />
            <p style={{ color: "#6b7280", marginBottom: 0 }}>{gateMessage}</p>
          </div>
        ) : null}

        {/* Property details modal */}
        <PropertyDetailsModal
          showModal={searchParams.has("modal", "propertydetails") && !!selectedProperty}
          setShowModal={handlePropertyDetailsModal}
          selectedProperty={selectedProperty}
          detailsResponse={detailsResponse}
          propertyDetails={propertyDetails}
          propertyDocuments={propertyDocuments}
          setPropertyDocuments={setPropertyDocuments}
          showDirections={showDirections}
          directions={directions}
          handleGetDirections={handleGetDirections}
          isPropertyRegistered={(prop) => isPropertyRegistered(prop, registrations)}
          getRegisterButtonLabel={(prop) => (isPropertyRegistered(prop, registrations) ? "Unregister" : "Register")}
          handleRegisterOrUnregisterClick={() => {
            if (!selectedProperty?.originalSaleId == null) {
              toast.triggerToast("No valid saleId to register/unregister");
              return;
            }
            handleRegisterOrUnregisterClick({
              saleId: selectedProperty.originalSaleId,
              auction: selectedProperty,
            });
          }}
          isPropertyFavorite={isPropertyFavoriteFunc}
          getFavoriteButtonLabel={(prop) => (isPropertyFavoriteFunc(prop) ? "Unfavorite" : "Favorite")}
          handleFavoriteClick={() => {
            if (!selectedProperty) return;
            if (isPropertyFavoriteFunc(selectedProperty)) {
              const fav = findFavorite(favorites, selectedProperty);
              if (!fav?.favorite_id) {
                toast.triggerToast("No favorite_id to remove.");
                return;
              }
              handleRemoveFavorite(fav.favorite_id);
            } else {
              handleAddFavorite(selectedProperty);
            }
          }}
          isLoadingInfo={{ isLoaded, loadError }}
          countyDisplayName={countyDisplayName}
        />

        <PropertyMapModal
          showModal={searchParams.has("modal", "propertymap") && !!selectedProperty}
          setShowModal={handleMapModal}
          onHide={() => handleMapModal(false, null)}
          selectedProperty={selectedProperty}
          detailsResponse={detailsResponse}
          propertyDetails={propertyDetails}
          propertyDocuments={propertyDocuments}
          showDirections={showDirections}
          directions={directions}
          handleGetDirections={handleGetDirections}
          isPropertyRegistered={(prop) => isPropertyRegistered(prop, registrations)}
          getRegisterButtonLabel={(prop) => (isPropertyRegistered(prop, registrations) ? "Unregister" : "Register")}
          handleRegisterOrUnregisterClick={() => {
            if (!selectedProperty?.originalSaleId == null) {
              toast.triggerToast("No valid saleId to register/unregister");
              return;
            }
            handleRegisterOrUnregisterClick({
              saleId: selectedProperty.originalSaleId,
              auction: selectedProperty,
            });
          }}
          isPropertyFavorite={isPropertyFavoriteFunc}
          getFavoriteButtonLabel={(prop) => (isPropertyFavoriteFunc(prop) ? "Unfavorite" : "Favorite")}
          handleFavoriteClick={() => {
            if (!selectedProperty) return;
            if (isPropertyFavoriteFunc(selectedProperty)) {
              const fav = findFavorite(favorites, selectedProperty);
              if (!fav?.favorite_id) {
                toast.triggerToast("No favorite_id to remove.");
                return;
              }
              handleRemoveFavorite(fav.favorite_id);
            } else {
              handleAddFavorite(selectedProperty);
            }
          }}
          isLoadingInfo={{ isLoaded, loadError }}
          countyDisplayName={countyDisplayName}
        />
      </div>

      {/* SB 156 Documents modal */}
      <Modal show={showDocumentModal} onHide={() => setShowDocumentModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>SB 156 Documents</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DocumentsTable />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDocumentModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Auction list modal */}
      <Modal show={showAuctionModal} onHide={() => setShowAuctionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Auction List</Modal.Title>
        </Modal.Header>
        <Modal.Body />
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAuctionModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Properties;
